import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { customerOrderApi, CustomerOrder, CustomerOrderStatus } from '@/services/customerOrderApi';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import SimpleSelect from '@/components/common/SimpleSelect';

const VALID_TRANSITIONS: Record<CustomerOrderStatus, CustomerOrderStatus[]> = {
  NEW: ['ORDERED', 'CANCELLED'],
  ORDERED: ['RECEIVED', 'CANCELLED'],
  RECEIVED: ['NOTIFIED', 'DELIVERED', 'CANCELLED'],
  NOTIFIED: ['DELIVERED', 'CANCELLED'],
  DELIVERED: [],
  CANCELLED: [],
};

const STATUS_LABELS: Record<CustomerOrderStatus, string> = {
  NEW: 'New',
  ORDERED: 'Ordered',
  RECEIVED: 'Received',
  NOTIFIED: 'Notified',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  order: CustomerOrder;
}

export function StatusUpdateModal({ isOpen, onClose, order }: Props) {
  const queryClient = useQueryClient();
  const [newStatus, setNewStatus] = useState<CustomerOrderStatus | ''>('');
  const [notes, setNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  const allowedStatuses = VALID_TRANSITIONS[order.status] || [];

  const mutation = useMutation({
    mutationFn: () =>
      customerOrderApi.updateStatus(
        order.id,
        newStatus as CustomerOrderStatus,
        notes || undefined,
        cancelReason || undefined
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-customer-orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin-customer-order-stats'] });
      queryClient.invalidateQueries({ queryKey: ['customer-order', order.id] });
      toast.success(`Order status updated to ${STATUS_LABELS[newStatus as CustomerOrderStatus]}`);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to update status');
    },
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Update Status</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-gray-600">
            Order: <span className="font-medium text-gray-900">{order.orderNumber}</span>
            <span className="mx-2">|</span>
            Current: <span className="font-medium">{STATUS_LABELS[order.status]}</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Status <span className="text-red-500">*</span>
            </label>
            <SimpleSelect
              value={newStatus}
              onChange={(v) => setNewStatus(v as CustomerOrderStatus)}
              options={[
                { value: '', label: 'Select status...' },
                ...allowedStatuses.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
              ]}
              placeholder="Select status..."
              aria-label="New status"
              wrapperClassName="w-full min-w-0"
              className="focus:!ring-purple-500 focus:!border-purple-500"
            />
          </div>

          {newStatus === 'CANCELLED' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cancel Reason</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={2}
                placeholder="Reason for cancellation..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional notes..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 resize-none"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => mutation.mutate()}
              disabled={!newStatus || mutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:bg-purple-400"
            >
              {mutation.isPending ? 'Updating...' : 'Update Status'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
